import styled, { css } from 'styled-components/macro';
import {
  TEXT_ORIENTATION,
  TEXT_ALIGNMENT,
  FONT_CASE_KEYWORD,
  FONT_WEIGHT_KEYWORD,
  GRADIENT_TYPES,
} from '../../../constants';

export const LandingPageDesignPreviewContainer = styled.div<{
  fontFamily: string;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
`;

// HEADER
export const HeaderContainer = styled.div<{
  headerBackgroundColor: string;
  showHeaderShadow: number;
}>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
  padding: 6px 14px;
  z-index: 1;

  background: ${({ headerBackgroundColor }) =>
    headerBackgroundColor
      ? headerBackgroundColor
      : props => props.theme.colors.neutral[40]};

  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0px;
    width: 100%;
    height: 6px;
    pointer-events: none;
    box-shadow: ${({ showHeaderShadow }) =>
      showHeaderShadow ? 'rgba(0, 0, 0, 0.12) 0px 2px 4px -1px' : 'none'};
    background: transparent;
  }
`;

export const LogoImage = styled.img`
  object-fit: contain;
  transform-origin: center left;
  display: flex;
  max-width: 100px;
  max-height: 10px;
`;

export const LabelContainer = styled.div<{
  headerContentOrientation: TEXT_ORIENTATION;
  headerContentAlignment: TEXT_ALIGNMENT;
}>`
  display: flex;
  margin-left: auto;
  align-self: center;
  flex-direction: ${({ headerContentOrientation }) =>
    headerContentOrientation === TEXT_ORIENTATION.HORIZONTAL
      ? 'row'
      : 'column'};
  align-items: ${({ headerContentAlignment }) =>
    headerContentAlignment === TEXT_ALIGNMENT.RIGHT
      ? 'flex-end'
      : headerContentAlignment === TEXT_ALIGNMENT.LEFT
        ? 'flex-start'
        : TEXT_ALIGNMENT.CENTER};
  gap: ${({ headerContentOrientation }) =>
    headerContentOrientation === TEXT_ORIENTATION.HORIZONTAL ? '6px' : '2px'};
`;

export const Label = styled.p<{
  color: string;
  headerFontCase: FONT_CASE_KEYWORD;
  headerFontWeight: FONT_WEIGHT_KEYWORD;
  headerFontFamily: string;
}>`
  font-size: 3px;
  letter-spacing: 0px;
  margin: 0;

  color: ${({ color }) => color};
  font-family: ${({ headerFontFamily }) => headerFontFamily};
  text-transform: ${({ headerFontCase }) => headerFontCase};
  font-weight: ${({ headerFontWeight }) => parseInt(headerFontWeight)};
`;

// FOOTER
export const FooterContainer = styled.div<{
  footerBackgroundColor?: string;
  showFooterShadow?: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 15px 0px;
  flex-shrink: 0;
  background: ${({ footerBackgroundColor }) => footerBackgroundColor};

  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 6px;
    pointer-events: none;
    background: transparent;
    box-shadow: ${({ showFooterShadow }) =>
      showFooterShadow ? '0 -2px 4px -1px rgba(0, 0, 0, 0.12)' : 'none'};
  }
`;

// BODY
export const BodyContainer = styled.div<{
  bodyBackgroundColor: string;
  bodyBackgroundImage: string;
}>`
  display: flex;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  align-items: center;

  padding: 10px 0 10px 0;
  flex-direction: row;
  gap: 12.5px;

  flex: 1;
  width: 100%;
  overflow: auto;

  background-color: ${({ bodyBackgroundImage, bodyBackgroundColor }) =>
    bodyBackgroundImage ? 'transparent' : bodyBackgroundColor};
  position: relative;
`;

export const VideoPlayerImage = styled.img`
  display: block;
  width: 100%;
  height: 50px;

  @media (max-width: 768px) {
    max-width: 100px;
  }

  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

export const GradientOverlay = styled.div<{ gradient: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;

  ${({ gradient }) =>
    gradient === GRADIENT_TYPES.DARK
      ? css`
          background: linear-gradient(
            89.91deg,
            rgba(0, 0, 0, 0.4) 0.09%,
            rgba(0, 0, 0, 0) 99.92%
          );
        `
      : css`
          background: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.4),
            rgba(255, 255, 255, 0.1)
          );
        `}
`;

export const StyledBackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  z-index: 0;
`;

export const CtaButton = styled.button<{
  buttonColor: string;
  buttonHoverColor: string;
  buttonBorderColor: string;
  buttonBorderHoverColor: string;
  showButtonBorder: number;
  buttonTextColor: string;
  buttonTextHoverColor: string;
  buttonFontCase: FONT_CASE_KEYWORD;
  buttonFontWeight: FONT_WEIGHT_KEYWORD;
  buttonFontSize: number;
  buttonFontFamily: string;
  buttonCornerRadius: number;
  buttonPaddingY: number;
  buttonPaddingX: number;
}>`
  max-height: 10px;
  box-sizing: border-box;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: ${({ buttonFontSize }) => `${buttonFontSize * 0.2}px`};
  font-family: ${({ buttonFontFamily }) => buttonFontFamily};
  text-transform: ${({ buttonFontCase }) => buttonFontCase};
  font-weight: ${({ buttonFontWeight }) => parseInt(buttonFontWeight)};
  background: ${({ buttonColor }) => buttonColor};
  border: ${({ showButtonBorder, buttonBorderColor }) =>
    showButtonBorder ? `2px solid ${buttonBorderColor}` : 'none'};
  border-radius: ${({ buttonCornerRadius }) => `${buttonCornerRadius}px`};
  padding: ${({ buttonPaddingY, buttonPaddingX }) =>
    `${buttonPaddingY * 0.2}px ${buttonPaddingX * 0.2}px`};

  color: ${({ buttonTextColor }) => buttonTextColor};
`;
